import {
  Button,
  CounterBadge,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  makeStyles,
  mergeClasses,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  Add16Regular,
  Delete16Regular,
  Edit16Regular,
  Folder20Regular,
  FolderOpen20Filled,
} from '@fluentui/react-icons';
import { useDroppable } from '@dnd-kit/core';
import { motion, AnimatePresence } from 'framer-motion';
import React, { useState } from 'react';
import {
  useFoldersQuery,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useRenameFolderMutation,
} from '../../queries/folders';
import { Folder, FOLDER_COLORS } from '@axys-notes/common';
import { ColorPicker } from '../color-picker';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
  },
  folderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusLarge,
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    marginBottom: '2px',
    border: '1px solid transparent',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateX(2px)',
    },
  },
  folderItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    boxShadow: `0 1px 4px ${tokens.colorNeutralShadowAmbient}`,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  folderItemDropTarget: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `2px dashed ${tokens.colorPaletteGreenBorder2}`,
    transform: 'scale(1.02)',
  },
  folderIcon: {
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
    transition: 'transform 0.2s ease',
  },
  folderName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  folderItemRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
  },
  divider: {
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${tokens.colorNeutralStroke2}, transparent)`,
    margin: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
  },
  sectionLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    fontWeight: tokens.fontWeightSemibold,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    marginTop: tokens.spacingVerticalXS,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
});

interface FolderSidebarProps {
  selectedFolderId?: string;
  onFolderSelect: (folderId?: string) => void;
  isDragging?: boolean;
}

interface FolderItemProps {
  folderId: string;
  icon: React.ReactNode;
  name: string;
  count?: number;
  color?: string;
  isSelected: boolean;
  isDragging?: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const FolderItem = ({
  folderId,
  icon,
  name,
  count,
  color,
  isSelected,
  isDragging,
  onClick,
  onEdit,
  onDelete,
}: FolderItemProps) => {
  const styles = useStyles();
  const [isHovered, setIsHovered] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: folderId,
  });

  return (
    <motion.div
      ref={setNodeRef}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      className={mergeClasses(
        styles.folderItem,
        isSelected && styles.folderItemSelected,
        isDragging && isOver && styles.folderItemDropTarget
      )}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span className={styles.folderIcon} style={color ? { color } : undefined}>{icon}</span>
      <Text className={styles.folderName}>{name}</Text>
      <div className={styles.folderItemRight}>
        {isHovered && (onEdit || onDelete) ? (
          <>
            {onEdit && (
              <Button
                appearance="subtle"
                size="small"
                icon={<Edit16Regular />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              />
            )}
            {onDelete && (
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete16Regular />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              />
            )}
          </>
        ) : (
          count !== undefined && count > 0 && (
            <CounterBadge count={count} size="small" appearance="filled" color="brand" />
          )
        )}
      </div>
    </motion.div>
  );
};

export const FolderSidebar = ({
  selectedFolderId,
  onFolderSelect,
  isDragging,
}: FolderSidebarProps) => {
  const styles = useStyles();
  const { data, isPending, error } = useFoldersQuery();
  const createFolderMutation = useCreateFolderMutation();
  const deleteFolderMutation = useDeleteFolderMutation();
  const renameFolderMutation = useRenameFolderMutation();

  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState<string>(FOLDER_COLORS[0]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string>(FOLDER_COLORS[0]);
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null);

  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      await createFolderMutation.mutateAsync({
        name: newFolderName.trim(),
        color: newFolderColor,
      });
      setNewFolderName('');
      setNewFolderColor(FOLDER_COLORS[0]);
      setIsCreateDialogOpen(false);
    }
  };

  const handleUpdateFolder = async () => {
    if (editingFolder && editName.trim()) {
      await renameFolderMutation.mutateAsync({
        folderId: editingFolder.id,
        name: editName.trim(),
        color: editColor,
      });
      setEditingFolder(null);
      setEditName('');
      setEditColor(FOLDER_COLORS[0]);
    }
  };

  const handleConfirmDelete = async () => {
    if (deletingFolderId) {
      await deleteFolderMutation.mutateAsync(deletingFolderId);
      if (selectedFolderId === deletingFolderId) {
        onFolderSelect(undefined);
      }
      setDeletingFolderId(null);
    }
  };

  const getFolderIcon = (folderId: string | undefined, isSelected: boolean) => {
    if (isSelected) {
      return <FolderOpen20Filled />;
    }
    return <Folder20Regular />;
  };

  const folderToDelete = data?.folders?.find((f) => f.id === deletingFolderId);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.headerTitle}>Dossiers</Text>
        <Button
          appearance="subtle"
          size="small"
          icon={<Add16Regular />}
          onClick={() => setIsCreateDialogOpen(true)}
        />
      </div>

      {isPending ? (
        <div className={styles.loadingContainer}>
          <Spinner size="small" />
        </div>
      ) : error ? (
        <div className={styles.loadingContainer}>
          <Text>Erreur de chargement</Text>
        </div>
      ) : (
        <div className={styles.list}>
          <FolderItem
            folderId="all"
            icon={getFolderIcon(undefined, !selectedFolderId)}
            name="Toutes les réunions"
            isSelected={!selectedFolderId}
            isDragging={isDragging}
            onClick={() => onFolderSelect(undefined)}
          />

          {data?.folders && data.folders.length > 0 && (
            <>
              <Text className={styles.sectionLabel}>Mes dossiers</Text>
              <AnimatePresence>
                {data.folders.map((folder) => (
                  <FolderItem
                    key={folder.id}
                    folderId={folder.id}
                    icon={getFolderIcon(folder.id, selectedFolderId === folder.id)}
                    name={folder.name}
                    count={folder.recordingsCount}
                    color={folder.color}
                    isSelected={selectedFolderId === folder.id}
                    isDragging={isDragging}
                    onClick={() => onFolderSelect(folder.id)}
                    onEdit={() => {
                      setEditingFolder(folder);
                      setEditName(folder.name);
                      setEditColor(folder.color || FOLDER_COLORS[0]);
                    }}
                    onDelete={() => setDeletingFolderId(folder.id)}
                  />
                ))}
              </AnimatePresence>
            </>
          )}

          <div className={styles.divider} />

          <FolderItem
            folderId="unclassified"
            icon={getFolderIcon('unclassified', selectedFolderId === 'unclassified')}
            name="Non classées"
            count={data?.unclassifiedCount}
            isSelected={selectedFolderId === 'unclassified'}
            isDragging={isDragging}
            onClick={() => onFolderSelect('unclassified')}
          />
        </div>
      )}

      {/* Create folder dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(_, data) => setIsCreateDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Creer un dossier</DialogTitle>
            <DialogContent>
              <Input
                placeholder="Nom du dossier"
                value={newFolderName}
                onChange={(_, data) => setNewFolderName(data.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateFolder();
                }}
                style={{ width: '100%', marginBottom: tokens.spacingVerticalM }}
              />
              <Text size={200} style={{ marginBottom: tokens.spacingVerticalXS, display: 'block' }}>
                Couleur
              </Text>
              <ColorPicker
                selectedColor={newFolderColor}
                onChange={setNewFolderColor}
              />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Annuler</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || createFolderMutation.isPending}
              >
                Creer
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog
        open={!!editingFolder}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setEditingFolder(null);
            setEditName('');
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Renommer le dossier</DialogTitle>
            <DialogContent>
              <Input
                placeholder="Nouveau nom"
                value={editName}
                onChange={(_, data) => setEditName(data.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUpdateFolder();
                }}
                style={{ width: '100%', marginBottom: tokens.spacingVerticalM }}
              />
              <Text size={200} style={{ marginBottom: tokens.spacingVerticalXS, display: 'block' }}>
                Couleur
              </Text>
              <ColorPicker
                selectedColor={editColor}
                onChange={setEditColor}
              />
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setEditingFolder(null);
                  setEditName('');
                }}
              >
                Annuler
              </Button>
              <Button
                appearance="primary"
                onClick={handleUpdateFolder}
                disabled={!editName.trim() || renameFolderMutation.isPending}
              >
                Enregistrer
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete folder confirmation dialog */}
      <Dialog
        open={!!deletingFolderId}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setDeletingFolderId(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Supprimer le dossier</DialogTitle>
            <DialogContent>
              <Text>
                Etes-vous sur de vouloir supprimer le dossier "{folderToDelete?.name}" ?
              </Text>
              <br />
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Les réunions contenues dans ce dossier ne seront pas supprimees.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeletingFolderId(null)}
              >
                Annuler
              </Button>
              <Button
                appearance="primary"
                onClick={handleConfirmDelete}
                disabled={deleteFolderMutation.isPending}
                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
              >
                {deleteFolderMutation.isPending ? 'Suppression...' : 'Supprimer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
